import { AlertTriangle, ShieldCheck, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import {
  CAPACITY_STATUS_LABEL,
  capacityStatusOf,
} from '@obraplan/core/domain/planning/capacity-calculator';
import type { BoardDayTotal } from '@obraplan/core/domain/planning/weekly-board';
import { Hours, QUARTERS_PER_HOUR } from '@obraplan/core/domain/shared/hours';
import { cn } from '@/lib/cn';
import { CELL_TONE } from './planner-format';

/**
 * As células do planejamento semanal — horas, meta e total do dia.
 *
 * Vivem fora da grade porque **a grade não é o único jeito de mostrar a semana**: no
 * celular ela vira uma lista de cartões, e as duas apresentações precisam usar
 * exatamente a mesma célula. Duplicá-la faria a validação de 15 minutos, o aviso de
 * excesso e o gesto de "campo vazio remove" existirem em duas versões — e a segunda
 * começaria a divergir na primeira correção.
 */

export interface AllocationEdit {
  readonly allocationId: string;
  readonly quarterHours: number;
  readonly version: number;
  /** `null` limpa a meta do dia; ausente deixa como está. */
  readonly plannedQuantity?: number | null;
}

export interface AllocationCreate {
  /** O serviço da linha: digitar horas numa célula vazia continua na mesma linha. */
  readonly serviceId?: string;
  readonly resourceId: string;
  readonly activityId: string;
  readonly date: string;
  readonly quarterHours: number;
}

export function DayTotalCell({ total }: { total: BoardDayTotal }) {
  if (total.plannedQuarterHours === 0 && (total.capacityQuarterHours ?? 0) === 0) {
    return <span className="text-xs text-(--color-content-muted)">—</span>;
  }

  const planned = Hours.ofQuarters(total.plannedQuarterHours).format();
  const capacity =
    total.capacityQuarterHours === undefined
      ? null
      : Hours.ofQuarters(total.capacityQuarterHours).format();

  return (
    <span className={cn('tabular text-xs', CELL_TONE[total.status])}>
      {capacity === null ? planned : `${planned} / ${capacity}`}
      {total.status === 'EXCEEDED' && (
        <span className="ml-1 inline-flex items-center gap-0.5">
          <AlertTriangle className="inline size-3" aria-hidden="true" />
          <span className="sr-only">{CAPACITY_STATUS_LABEL.EXCEEDED}</span>
        </span>
      )}
    </span>
  );
}

export interface QuantityCellProps {
  plannedQuantity: number | undefined;
  executedQuantity: number | undefined;
  unitName: string;
  decimals: number;
  resourceName: string;
  activityLabel: string;
  /** O dia, no rótulo do campo: sem ele, os sete campos da semana têm o mesmo nome. */
  dayLabel: string;
  editable: boolean;
  /**
   * A hora do dia foi digitada e a alocação **ainda não existe** no servidor.
   *
   * O campo já aceita a meta, e a guarda até a alocação chegar — quem digita a hora e clica
   * logo embaixo não pode encontrar um "—" de texto no lugar do campo.
   */
  pending?: boolean;
  onCommit: (plannedQuantity: number | null) => void;
}

/**
 * A meta de um dia, na unidade da frente.
 *
 * Só é editável onde **há hora**: meta sem ninguém alocado seria uma promessa sem dono, e
 * o dia vazio mostra `—` em vez de um campo que não leva a nada.
 *
 * "Há hora" inclui a que acabou de ser digitada. A alocação nasce no servidor quando a
 * célula de horas perde o foco, e o campo da meta só existia depois da volta — quem
 * digitava a hora e clicava logo na meta acertava o "—", o clique se perdia e o número
 * digitado em seguida ia para lugar nenhum. Agora o campo aparece com a hora, e a meta
 * confirmada antes de a alocação existir espera por ela (`pending`).
 *
 * Quando já há medição lançada na conferência, ela aparece embaixo. É a leitura que o
 * encarregado faz no meio da semana — o que prometi × o que saiu — sem trocar de tela.
 */
export function QuantityCell({
  plannedQuantity,
  executedQuantity,
  unitName,
  decimals,
  resourceName,
  activityLabel,
  dayLabel,
  editable,
  pending = false,
  onCommit,
}: QuantityCellProps) {
  const asText = plannedQuantity === undefined ? '' : String(plannedQuantity);
  const [draft, setDraft] = useState(asText);
  /** A meta confirmada enquanto a alocação não existia — sobe quando ela chegar. */
  const aguardando = useRef<number | null | undefined>(undefined);

  useEffect(() => {
    setDraft(asText);
  }, [asText]);

  useEffect(() => {
    // Sem campo, não há o que esperar: a hora foi apagada antes de ir ao servidor.
    if (!editable) {
      aguardando.current = undefined;
      return;
    }
    if (pending || aguardando.current === undefined) return;
    const valor = aguardando.current;
    aguardando.current = undefined;
    onCommit(valor);
    // `onCommit` é uma closure nova a cada render; o que dispara a gravação é a alocação
    // passar a existir, e não a identidade da função.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending, editable]);

  if (!editable) {
    return (
      <span className="tabular text-xs text-(--color-content-muted)">
        {plannedQuantity === undefined ? '—' : plannedQuantity.toFixed(decimals)}
      </span>
    );
  }

  const commit = () => {
    const trimmed = draft.trim();
    if (trimmed === asText.trim()) {
      aguardando.current = undefined;
      return;
    }
    const value = trimmed === '' ? null : Number(trimmed.replace(',', '.'));
    if (value !== null && (!Number.isFinite(value) || value < 0)) {
      setDraft(asText);
      return;
    }
    if (pending) {
      aguardando.current = value;
      return;
    }
    onCommit(value);
  };

  return (
    <div className="flex flex-col items-center">
      <input
        type="number"
        inputMode="decimal"
        min={0}
        step={10 ** -decimals}
        aria-label={`Meta de ${activityLabel} para ${resourceName} — ${dayLabel}, em ${unitName}`}
        className="tabular h-11 w-full rounded border border-(--color-border-subtle) bg-(--color-surface) px-1 text-center text-xs md:h-auto md:w-16 md:py-0.5"
        value={draft}
        onChange={(event) => {
          setDraft(event.target.value);
        }}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === 'Enter') event.currentTarget.blur();
          if (event.key === 'Escape') setDraft(asText);
        }}
      />
      {executedQuantity !== undefined && (
        <span className="tabular text-[0.65rem] text-(--color-content-muted)">
          saiu {executedQuantity.toFixed(decimals)}
        </span>
      )}
    </div>
  );
}

export interface HoursCellProps {
  quarterHours: number;
  dayTotal: BoardDayTotal;
  isWorkday: boolean;
  exceedsCapacity: boolean;
  justification: string | undefined;
  resourceName: string;
  activityLabel: string;
  dayLabel: string;
  editable: boolean;
  onCommit: (quarterHours: number) => void;
  onDelete?: () => void;
  /** As horas que estão no campo agora, gravadas ou não — é o que acende a meta do dia. */
  onDraftChange?: (quarterHours: number) => void;
}

/**
 * Célula de um dia, em incrementos de 15 minutos.
 *
 * O campo trabalha em horas decimais porque é assim que quem planeja escreve — `4,5` —,
 * mas o valor enviado é sempre inteiro de quarto de hora. Campo vazio numa célula que
 * tem alocação significa **remover**: é o gesto natural de quem tirou a pessoa daquele
 * dia, e obrigar a caçar um botão de lixeira seria pior.
 */
export function HoursCell({
  quarterHours,
  dayTotal,
  isWorkday,
  exceedsCapacity,
  justification,
  resourceName,
  activityLabel,
  dayLabel,
  editable,
  onCommit,
  onDelete,
  onDraftChange,
}: HoursCellProps) {
  const asText = quarterHours === 0 ? '' : String(quarterHours / QUARTERS_PER_HOUR);
  const [draft, setDraft] = useState(asText);

  // Atualização vinda do banco (outra aba, ou reversão de uma gravação recusada)
  // sobrescreve o rascunho: a célula mostra o que está gravado, não o que foi digitado.
  useEffect(() => {
    setDraft(quarterHours === 0 ? '' : String(quarterHours / QUARTERS_PER_HOUR));
  }, [quarterHours]);

  const trimmed = draft.trim();
  const parsedQuarters =
    trimmed === '' ? 0 : Math.round(Number(trimmed.replace(',', '.')) * QUARTERS_PER_HOUR);
  const valid = Number.isFinite(parsedQuarters) && parsedQuarters >= 0;
  const dirty = valid && parsedQuarters !== quarterHours;

  const digitadas = valid ? parsedQuarters : 0;
  useEffect(() => {
    onDraftChange?.(digitadas);
    // Avisa quando o número muda, e não quando a closure muda.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [digitadas]);

  // O mesmo cálculo do domínio, com o valor ainda em edição: o aviso aparece **antes** de
  // enviar, e nunca discorda da resposta do caso de uso.
  const previewStatus = capacityStatusOf(
    dayTotal.capacityQuarterHours,
    dayTotal.plannedQuarterHours - quarterHours + (valid ? parsedQuarters : quarterHours),
  );

  const commit = () => {
    if (!valid || parsedQuarters === quarterHours) {
      setDraft(asText);
      return;
    }
    if (parsedQuarters === 0 && quarterHours === 0) {
      setDraft('');
      return;
    }
    onCommit(parsedQuarters);
  };

  if (!editable) {
    return (
      <div className="flex items-center justify-center gap-1">
        <span
          className={cn('tabular text-sm', quarterHours === 0 && 'text-(--color-content-muted)')}
        >
          {quarterHours === 0 ? '—' : Hours.ofQuarters(quarterHours).format()}
        </span>
        {exceedsCapacity && <AuthorizedExceptionMark justification={justification} />}
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center gap-0.5">
      <input
        type="number"
        step={0.25}
        min={0}
        value={draft}
        placeholder="—"
        aria-label={`Horas de ${resourceName} em ${activityLabel} — ${dayLabel}`}
        aria-invalid={!valid}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === 'Enter') event.currentTarget.blur();
          if (event.key === 'Escape') setDraft(asText);
        }}
        className={cn(
          // 44px de altura no dedo, 32 no ponteiro: a mesma célula serve à lista de
          // cartões do celular e à grade do monitor.
          'tabular h-11 w-full rounded-md border border-(--color-border-subtle) bg-(--color-surface) px-1.5 text-center text-sm md:h-8 md:w-16',
          !isWorkday && 'bg-(--color-surface-muted)',
          CELL_TONE[previewStatus],
          previewStatus === 'EXCEEDED' && 'border-(--color-state-danger)',
        )}
      />
      {exceedsCapacity && <AuthorizedExceptionMark justification={justification} />}
      {dirty && previewStatus === 'EXCEEDED' && (
        <span role="status" className="sr-only">
          {CAPACITY_STATUS_LABEL.EXCEEDED}
        </span>
      )}
      {onDelete !== undefined && (
        <button
          type="button"
          onClick={onDelete}
          aria-label={`Remover ${resourceName} de ${activityLabel} em ${dayLabel}`}
          className="rounded-md p-0.5 text-(--color-content-muted) hover:bg-(--color-surface-muted) hover:text-(--color-state-danger)"
        >
          <Trash2 className="size-3" aria-hidden="true" />
        </button>
      )}
    </div>
  );
}

/**
 * A marca da **exceção autorizada** — história, não situação atual.
 *
 * `exceedsCapacity` registra que aquela alocação foi aceita acima da capacidade **naquele
 * momento**, com justificativa (RN-045). Ela não é recalculada depois, e não deve ser: é
 * o registro da autorização, e apagá-lo quando o dia volta a caber esconderia que alguém
 * precisou autorizar.
 *
 * O que estava errado era **como** ela aparecia. Desenhada em vermelho com triângulo de
 * alerta, a marca dizia "este dia está acima da jornada" — e continuava dizendo isso
 * depois de a jornada ser ajustada, ao lado de um total do dia que já mostrava `7h / 9h`.
 * A tela contradizia a si mesma, e a leitura natural era que o aviso tinha travado.
 *
 * Agora ela diz o que de fato é: houve uma exceção, este é o motivo. Quem sinaliza o
 * excesso de **hoje** é o total do dia e a cor do próprio campo, que saem de
 * `capacityStatusOf` e são recalculados a cada leitura.
 */
function AuthorizedExceptionMark({ justification }: { justification: string | undefined }) {
  return (
    <span
      className="inline-flex items-center text-(--color-content-muted)"
      title={
        justification === undefined
          ? 'Lançada acima da capacidade e autorizada na época.'
          : `Lançada acima da capacidade e autorizada na época. Motivo: ${justification}`
      }
    >
      <ShieldCheck className="size-3.5" aria-hidden="true" />
      <span className="sr-only">Exceção de capacidade autorizada</span>
    </span>
  );
}
